import { useMemo } from 'react'
import { FormikProvider, useFormik } from 'formik'
import { faStars } from '@fortawesome/pro-regular-svg-icons'
import { getFetchError } from '@/reducers/fetch'
import { useReselector } from '@/utils/sharedHooks'
import { useAppDispatch } from '@/utils/typeHelpers'
import { useAnalyticsTrack } from '@/features/amplitude'

import { getFinancialProfile } from '@/selectors/user.selectors'
import {
  UPDATE_FINANCIAL_PROFILE_KEY,
  updateFinancialProfile,
} from '@/actions/financialProfileActions'
import {
  TAX_PROFILE_STEP,
  TaxesProfileFlowAnswerProps,
  useTaxProfileSteps,
  withholdingRecommendedMinimumPercentage,
} from './helpers'
import {
  addCurrencyArray,
  centsToCurrency,
  dollarsToCents,
  dollarsToCurrency,
} from '@/utils/currencyHelpers'
import { Alert, Form, Link, Text } from '@/ui'
import { TypographyStyles } from '@/styles'

import { TaxProfileFormFooter } from './TaxProfileFormFooter'
import {
  TaxProfileAccordions,
  useTaxProfileAccordionCopy,
} from './TaxProfileAccordions'
import { ExamplePaystub } from './ExamplePaystub'

// TODO: Migrate to V2
import { getFieldName, makeNumberSchema } from '@/base'

export const SpouseWithholdingForm = ({
  formFlowAnswers,
  setFormFlowAnswers,
}: TaxesProfileFlowAnswerProps) => {
  const dispatch = useAppDispatch()
  const track = useAnalyticsTrack()
  const fp = useReselector(getFinancialProfile)

  // Only one of these can be true at a time but this cleans up the logic a bit and makes it easier to extend in future
  const totalSpouseWIncome = addCurrencyArray([
    centsToCurrency(fp?.spouseWIncomeInCents),
    centsToCurrency(fp?.estimatedAnnualSpouseW2IncomeInCents),
  ])

  const error = useReselector(
    getFetchError,
    UPDATE_FINANCIAL_PROFILE_KEY(fp?.id)
  )
  const { goToPreviousStep, goToNextStep } = useTaxProfileSteps(
    TAX_PROFILE_STEP.spouseWithholding
  )

  const formik = useFormik({
    enableReinitialize: true,
    validateOnMount: true,
    initialValues: {
      spouseFederalWithholding: formFlowAnswers.spouseFederalWithholding,
      spouseStateWithholding: formFlowAnswers.spouseStateWithholding,
    },
    onSubmit: async ({ spouseFederalWithholding, spouseStateWithholding }) => {
      const res = await dispatch(
        updateFinancialProfile(fp?.id, {
          spouseFederalWithholdingInCents: dollarsToCents(
            spouseFederalWithholding
          ),
          spouseStateWithholdingInCents: dollarsToCents(spouseStateWithholding),
        })
      )

      if (res) {
        setFormFlowAnswers({
          ...formFlowAnswers,
          spouseFederalWithholding,
          spouseStateWithholding,
        })
        track('spouse withholding submitted', {
          spouse_federal_withholding_in_cents: dollarsToCents(
            spouseFederalWithholding
          ),
          spouse_state_withholding_in_cents: dollarsToCents(
            spouseStateWithholding
          ),
          user_id: fp?.userId ?? null,
        })
        goToNextStep(res)
      }
    },
  })

  const { values, submitForm, isValid, isSubmitting } = formik

  const spouseFederalWithholdingSchema = makeNumberSchema({
    allowedDecimals: 2,
    required: false,
  })
    .test(
      'spouseFederalWithholdingValidation',
      "Your spouse's withholdings must be less than the W-2 income you shared on a previous step. Please double-check your spouse's paystub.",
      (spouseFederalWithholding) => {
        const withholding = dollarsToCurrency(spouseFederalWithholding).value

        return !withholding || totalSpouseWIncome > withholding
      }
    )
    .test(
      'spouseFederalWithholdingGreaterThanZeroValidation',
      'Withholdings cannot be $0. Please double-check your paystub.',
      (spouseFederalWithholding) => {
        //don't throw an error when they first arrive to the page
        if (!spouseFederalWithholding) {
          return true
        }
        const withholding = dollarsToCurrency(spouseFederalWithholding).value

        return withholding > 0
      }
    )

  const spouseStateWithholdingSchema = makeNumberSchema({
    allowedDecimals: 2,
  }).test(
    'spouseStateWithholdingValidation',
    "Your spouse's withholdings must be less than the W-2 income you shared on a previous step. Please double-check your spouse's paystub.",
    (spouseStateWithholding) => {
      const withholding = dollarsToCurrency(spouseStateWithholding).value

      return !withholding || totalSpouseWIncome > withholding
    }
  )

  const withholdingPercentage = useMemo(
    () =>
      Math.floor(
        (Number(values.spouseFederalWithholding) / totalSpouseWIncome) * 100
      ),
    [values.spouseFederalWithholding, totalSpouseWIncome]
  )

  const faqCopy = useTaxProfileAccordionCopy()

  return (
    <FormikProvider value={formik}>
      <div className="w-full space-y-6">
        <ExamplePaystub zoomTo="withholdings" />

        <Text
          className="max-w-3xl mx-auto text-center"
          variant={TypographyStyles.DISPLAY_SM}
        >
          What are your spouse’s year-to-date W-2 tax withholdings?
        </Text>

        <Text
          className="max-w-3xl mx-auto text-center"
          variant={TypographyStyles.BODY_LG}
        >
          These are the total amounts withheld from <b>all</b> of your spouse’s
          W-2 wages so far this year. This is on their paystub.
        </Text>

        {error && (
          <Alert
            className="max-w-3xl mx-auto"
            variant="error"
            description={error.message}
          />
        )}

        <div className="max-w-2xl mx-auto">
          <Form.Input
            name={getFieldName<typeof values>('spouseFederalWithholding')}
            label="Spouse’s Year-to-Date Federal Tax Withholding"
            required
            componentType="currency"
            schema={spouseFederalWithholdingSchema}
          />
        </div>

        {Boolean(
          values.spouseFederalWithholding &&
          withholdingPercentage < withholdingRecommendedMinimumPercentage
        ) && (
          <div className="max-w-2xl mx-auto">
            <Alert
              customIconName={faStars}
              title={`Your spouse is withholding ${withholdingPercentage}% of their income`}
            >
              If your spouse withholds less than{' '}
              {withholdingRecommendedMinimumPercentage}% of their income, they
              will likely owe taxes at the end of the year. Learn how to
              calculate your spouse’s withholdings{' '}
              <Link
                href="https://apps.irs.gov/app/tax-withholding-estimator"
                newPage
                className="display-inline"
              >
                here
              </Link>
            </Alert>
          </div>
        )}

        <div className="max-w-2xl mx-auto">
          <Form.Input
            name={getFieldName<typeof values>('spouseStateWithholding')}
            label="Spouse’s Year-to-Date State Tax Withholding"
            required
            componentType="currency"
            schema={spouseStateWithholdingSchema}
          />
        </div>
        <TaxProfileAccordions
          faqs={[
            faqCopy.whyShareSpouseTaxWithholdings,
            faqCopy.whatIfHomeStateNoTax,
            faqCopy.whatAreSpouseYTDTaxWithholdings,
          ]}
        />

        <TaxProfileFormFooter
          submitOrContinue={submitForm}
          goToPreviousStep={goToPreviousStep}
          continueDisabled={!isValid || isSubmitting}
          loading={isSubmitting}
        />
      </div>
    </FormikProvider>
  )
}
